import React, { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

// Components
import { Card } from "../../Card/Card";
import { TreatmentSelect } from "../../FormElements/Select/TreatmentSelect";
import { InformationCardWithFiltersReset } from "../../InformationCard/InformationCardWithFiltersReset";
import { InformationCardWithFilterDoctors } from "../../InformationCard/InformationCardWithFilterDoctors";
import { StepLayout } from "../StepLayout/StepLayout";
import { QuickBookingSection } from "./QuickBookingSection/QuickBookingSection";
import { BookingSearch } from "../../BookingSearch/BookingSearch";

// Helpers
import { GtmService } from "../../GtmService";
import { setStorage } from "../../Helpers/formFunctions";
import { doctorInitialState } from "../../Helpers/initialState/doctorInitialState";
import { locationInitialState } from "../../Helpers/initialState/locationInitialState";
import { RootDataType } from "../../Helpers/interfaces";
import {
  findClosestParentContainerWithSpecializationName,
  findTreatmentById,
  isServiceContainer,
  isTreatmentContainer,
  selectContainer,
} from "../../Helpers/treatmentsHelpers";
import { isQuickBookingAvailable } from "./QuickBookingSection/QuickBookingSectionHelpers";
import { isLocationSelected } from "../../Helpers/locationHelpers";
import {
  FormDispatch,
  FormFields,
  FormReducer,
  FormStateType,
} from "../../Helpers/formTypes";

// Types & Models
import { ServiceContainerDto } from "@apiModels/ServiceContainerDto.csharp";
import { TreatmentsContainerDto } from "@apiModels/TreatmentsContainerDto.csharp";
import { QuickAppointmentSetupDto } from "../../../BookingButton/QuickAppointmentSetupDto.csharp";

// Hooks
import classNames from "classnames";
import { useBookingContext } from "../../../BookingButton/BookingContext/BookingContext";
import { LocationDropdown } from "../../FormElements/Dropdown/LocationDropdown/LocationDropdown";
import { StepName } from "../../Hooks/form";
import { useFilteredLocationsForTreatment } from "../../Hooks/useFilteredLocationsForTreatment";
import { useFilteredTreatments } from "../../Hooks/useFilteredTreatments";
import { useLabels } from "../../Hooks/useLabels";
import { useLocationDropdown } from "../../Hooks/useLocationAndDoctorSelection";
import { useFeatureFlags } from "../../../../Context/FeatureFlagsContext";

export interface InitialStepType {
  state: FormStateType;
  dispatch: FormDispatch;
  nextStep: () => void;
  setStepByName: (stepName: StepName) => void;
  rootData: RootDataType;
  quickAppointmentSetup?: QuickAppointmentSetupDto;
}

export const InitialStep: FC<InitialStepType> = ({
  state,
  dispatch,
  nextStep,
  setStepByName,
  rootData,
  quickAppointmentSetup,
}) => {
  const { bookingLabels } = useLabels();
  const [openedArea, setOpenedArea] = useState<string>();
  const openedAreaRef = useRef<HTMLDivElement>(null);
  const [wasOpenedByUser, setWasOpenedByUser] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { newBookingFilterValues } = useBookingContext();

  const { setValue } = useForm<FormStateType>({
    defaultValues: state,
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const { filteredLocations, unavailableLocations } =
    useFilteredLocationsForTreatment(rootData.centers, undefined, state.doctor);

  const { isFeatureEnabled } = useFeatureFlags();
  const decision = isFeatureEnabled("enable_new_location_dropdown");
  const isBookingSearchEnabled = isFeatureEnabled("booking_search");

  const commonLabels = bookingLabels!.commonLabels;

  const { onLocationSelect, onDoctorSelect } = useLocationDropdown(
    rootData.centers,
    state,
    dispatch,
    setValue
  );

  const filteredTreatments = useFilteredTreatments(
    rootData.treatments,
    state.location,
    state.doctor,
    undefined,
    !!newBookingFilterValues?.doctorId
  );

  useLayoutEffect(() => {
    if (filteredTreatments.length === 1) {
      const treatment = filteredTreatments[0];

      // If there is only one treatment and it's a sub treatment, then select it
      if (
        !state.hasUserInteracted &&
        isTreatmentContainer(treatment) &&
        treatment.items.length === 1
      ) {
        const subTreatment = treatment.items[0];
        onTreatmentSelect(subTreatment);
      }

      setOpenedArea(filteredTreatments[0].containerId);
    } else {
      setOpenedArea(undefined);
    }
  }, [filteredTreatments]);

  useEffect(() => {
    if (openedArea && !wasOpenedByUser) {
      scrollToOpenedArea();
    }
  }, [openedArea]);

  const scrollToOpenedArea = () => {
    openedAreaRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const quickBookingService = rootData.services.find(
    (service) => service.id === quickAppointmentSetup?.serviceId
  );

  const filteredTreatmentsRootContainer = filteredTreatments[0];
  const onTreatmentSelectByUser = (
    treatment: TreatmentsContainerDto | ServiceContainerDto
  ) => {
    dispatch({
      type: FormReducer.update,
      field: FormFields.hasUserInteracted,
      payload: true,
    });
    onTreatmentSelect(treatment);
  };

  const onTreatmentSelect = (
    treatment: TreatmentsContainerDto | ServiceContainerDto
  ) => {
    const rootTreatment =
      findTreatmentById(rootData.treatments, treatment.containerId) ||
      treatment;

    selectContainer(
      rootTreatment,
      rootData.treatments,
      dispatch,
      state,
      setStepByName
    );

    if (isTreatmentContainer(rootTreatment)) {
      GtmService.initialStepSubmit(
        state.treatment.containerId,
        rootTreatment.containerId,
        state.specializationName,
        state.service.specializationId,
        state.service.bookingName,
        state.service.id,
        state.location.name,
        state.location.id,
        state.doctor.name,
        state.doctor.id
      );
    } else if (isServiceContainer(rootTreatment)) {
      GtmService.initialStepSubmit(
        state.treatment.containerId,
        rootTreatment.containerId,
        state.specializationName,
        rootTreatment.specializationId,
        rootTreatment.bookingName,
        rootTreatment.id,
        state.location.name,
        state.location.id,
        state.doctor.name,
        state.doctor.id
      );
    }
  };

  const onServiceSelect = (serviceContainer: ServiceContainerDto) => {
    const specializationName = findClosestParentContainerWithSpecializationName(
      serviceContainer,
      rootData.treatments
    );
    dispatch({
      type: FormReducer.update,
      field: FormFields.service,
      payload: serviceContainer,
    });
    dispatch({
      type: FormReducer.update,
      field: FormFields.treatment,
      payload: serviceContainer,
    });
    dispatch({
      type: FormReducer.update,
      field: FormFields.parentTreatment,
      payload: filteredTreatmentsRootContainer,
    });
    dispatch({
      type: FormReducer.update,
      field: FormFields.specializationName,
      payload: specializationName,
    });

    setStorage(state);
    setStepByName("AppointmentStep");
  };

  const isDoctorSelected = state.doctor.id && state.doctor.id != "-1";

  const handleResetFilters = () => {
    if (isDoctorSelected) {
      dispatch({
        type: FormReducer.update,
        field: FormFields.doctor,
        payload: doctorInitialState,
      });
    }
    if (isLocationSelected(state.location)) {
      dispatch({
        type: FormReducer.update,
        field: FormFields.location,
        payload: locationInitialState,
      });
    }
    setStorage(state);
  };

  const handleResetDoctorOnly = () => {
    if (isDoctorSelected) {
      dispatch({
        type: FormReducer.update,
        field: FormFields.doctor,
        payload: doctorInitialState,
      });
      setStorage({
        ...state,
        doctor: doctorInitialState,
      });
    }
  };

  return (
    <div
      className={classNames("InitialStep", {
        "InitialStep--isDecision": decision,
      })}
    >
      {decision ? (
        <div className="InitialStep__locationContainer">
          <>
            <h1>Timebestilling</h1>
            <LocationDropdown
              key={state.location.id}
              defaultValue={state.location}
              label={commonLabels.placeDropdownLabel}
              placeholder={commonLabels.placeDropdownPlaceholder}
              onSelect={onLocationSelect}
              locations={{ items: filteredLocations }}
              unavailableLocations={{ items: unavailableLocations }}
              doctor={state.doctor}
              onDoctorSelect={onDoctorSelect}
              service={state.service}
              additionalLabels={commonLabels}
            />
          </>
        </div>
      ) : null}
      <StepLayout>
        {!state.doctor.id && isBookingSearchEnabled && (
          <BookingSearch
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            rootData={rootData}
            state={state}
            dispatch={dispatch}
            setStepByName={setStepByName}
          />
        )}

        {searchValue === "" && (
          <>
            {isDoctorSelected && (
              <>
                {decision && (
                  <InformationCardWithFilterDoctors
                    doctor={state.doctor}
                    onResetFilters={handleResetDoctorOnly}
                  />
                )}

                {isLocationSelected(state.location) && !decision && (
                  <InformationCardWithFiltersReset
                    doctor={state.doctor}
                    location={state.location}
                    onResetFilters={handleResetFilters}
                  />
                )}
              </>
            )}

            {quickAppointmentSetup &&
              quickBookingService &&
              isQuickBookingAvailable(
                quickBookingService,
                state.doctor,
                state.location
              ) && (
                <QuickBookingSection
                  {...quickAppointmentSetup}
                  onSelect={() => onTreatmentSelectByUser(quickBookingService)}
                />
              )}

            <div className="InitialStep__fieldsContainer">
              {filteredTreatments.map((treatment) => (
                <div
                  ref={
                    treatment.containerId === openedArea ? openedAreaRef : null
                  }
                  key={treatment.containerId}
                  className="InitialStep__field"
                >
                  {isServiceContainer(treatment) && (
                    <button
                      className="CardList__button"
                      onClick={() => onTreatmentSelectByUser(treatment)}
                    >
                      <Card
                        className={"CardList__card"}
                        heading={treatment.bookingName}
                        description={treatment.description}
                        useInitialStepStyle={true}
                      />
                    </button>
                  )}

                  {isTreatmentContainer(treatment) && (
                    <TreatmentSelect
                      idName={treatment.containerId}
                      placeholder={{
                        bookingName: treatment.bookingName,
                        description: treatment.description,
                      }}
                      onSelect={onTreatmentSelectByUser}
                      treatments={treatment.items}
                      toggleOpen={() => {
                        setOpenedArea(
                          openedArea === treatment.containerId
                            ? undefined
                            : treatment.containerId
                        );
                        setWasOpenedByUser(true);
                      }}
                      isOpen={treatment.containerId === openedArea}
                    />
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </StepLayout>
    </div>
  );
};
